// src/components/GuardianWaitlistForm.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { joinWaitlist, getWaitlistEntries } from '../services/courseService';
import guardianService from '../services/guardianService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function GuardianWaitlistForm({ courseId, onWaitlistSuccess, existingBookings = [], failedStudents = [], customMessage }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [guardianStudents, setGuardianStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState('');
    const [studentName, setStudentName] = useState('');
    const [isNewStudent, setIsNewStudent] = useState(false);
    const [studentsOnWaitlist, setStudentsOnWaitlist] = useState([]);
    
    // For multi-student selection when booking failed for multiple
    const [selectedStudentsForWaitlist, setSelectedStudentsForWaitlist] = useState(
        failedStudents.length > 0 ? failedStudents.map(s => s.email) : []
    );
    const isMultiStudentMode = failedStudents.length > 1;
    
    console.log('GuardianWaitlistForm - Debug:', {
        failedStudents,
        failedStudentsLength: failedStudents.length,
        isMultiStudentMode,
        customMessage,
        userGroups: user?.groups,
        isGuardian: user?.groups?.includes('Guardian')
    });
    
    // Student information fields
    const [studentInfo, setStudentInfo] = useState({
        school: '',
        yearLevel: '',
        dateOfBirth: '',
        gender: '',
        medicalConditions: '',
        dietaryRequirements: '',
        swimmingAbility: ''
    });
    
    // Check if user is a guardian
    const isGuardian = user?.groups?.includes('Guardian');
    const userEmail = user?.email;

    useEffect(() => {
        if (isGuardian) {
            loadGuardianStudents();
            loadWaitlistEntries();
        }
    }, [isGuardian, courseId]);
    
    // Pre-select single failed student when component mounts
    useEffect(() => {
        if (failedStudents.length === 1 && !isMultiStudentMode) {
            const failedStudent = failedStudents[0];
            setSelectedStudent(failedStudent.email);
            setStudentName(failedStudent.name || failedStudent.firstName || '');
            // Pre-fill student info if available
            if (failedStudent.school) setStudentInfo(prev => ({...prev, school: failedStudent.school}));
            if (failedStudent.yearLevel) setStudentInfo(prev => ({...prev, yearLevel: failedStudent.yearLevel}));
            if (failedStudent.dateOfBirth) setStudentInfo(prev => ({...prev, dateOfBirth: failedStudent.dateOfBirth}));
            if (failedStudent.gender) setStudentInfo(prev => ({...prev, gender: failedStudent.gender}));
        }
    }, [failedStudents, isMultiStudentMode]);

    const loadGuardianStudents = async () => {
        try {
            const response = await guardianService.getStudents();
            if (response?.students) {
                // Filter out students who already have bookings for this course
                const bookedEmails = existingBookings.map(b => b.email?.toLowerCase());
                const availableStudents = response.students.filter(
                    s => !bookedEmails.includes(s.email?.toLowerCase())
                );
                setGuardianStudents(availableStudents);
            }
        } catch (error) {
            console.error('Failed to load guardian students:', error);
        }
    };

    const loadWaitlistEntries = async () => {
        if (!courseId) return;
        try {
            const entries = await getWaitlistEntries();
            // Filter for entries for this course
            const courseEntries = entries.filter(e => e.courseId === courseId);
            const studentEmails = courseEntries.map(e => e.studentEmail?.toLowerCase()).filter(Boolean);
            setStudentsOnWaitlist(studentEmails);
        } catch (error) {
            console.error('Failed to load waitlist entries:', error);
            setStudentsOnWaitlist([]);
        }
    };

    const handleWaitlist = async (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        setLoading(true);
        setError(null);

        try {
            // Handle single failed student - one-click scenario
            if (failedStudents.length === 1 && !isMultiStudentMode) {
                const student = failedStudents[0];
                const waitlistData = {
                    courseId,
                    email: student.email,
                    studentName: student.name || student.firstName,
                    guardianEmail: userEmail,
                    isGuardianBooking: true,
                    formData: {
                        student_name: student.name || student.firstName,
                        parent_email: userEmail,
                        gender: student.gender,
                        school: student.school || '',
                        yearLevel: student.yearLevel || '',
                        dateOfBirth: student.dateOfBirth || ''
                    }
                };
                
                const result = await joinWaitlist(waitlistData);
                if (result) {
                    toast.success(`${student.name || 'Student'} has been added to the waitlist!`);
                    if (onWaitlistSuccess) {
                        onWaitlistSuccess();
                    }
                }
                setLoading(false);
                return;
            }
            
            // Multi-student mode OR multiple failed students - add all
            if (isMultiStudentMode || failedStudents.length > 1) {
                // If we have failed students, use them all; otherwise use selected ones
                const studentsToAdd = failedStudents.length > 1 
                    ? failedStudents 
                    : selectedStudentsForWaitlist.map(email => failedStudents.find(s => s.email === email)).filter(Boolean);
                
                if (studentsToAdd.length === 0) {
                    setError('Please select at least one student to add to the waitlist');
                    setLoading(false);
                    return;
                }
                
                const results = [];
                for (const student of studentsToAdd) {
                    
                    const waitlistData = {
                        courseId,
                        email: student.email,
                        studentName: student.name,
                        guardianEmail: userEmail,
                        isGuardianBooking: true,
                        formData: {
                            student_name: student.name,
                            parent_email: userEmail,
                            gender: student.gender
                        }
                    };
                    
                    try {
                        await joinWaitlist(waitlistData);
                        results.push({ success: true, student: student.name });
                    } catch (err) {
                        results.push({ success: false, student: student.name, error: err.message });
                    }
                }
                
                // Show results
                const successCount = results.filter(r => r.success).length;
                if (successCount > 0) {
                    toast.success(`Added ${successCount} student(s) to waitlist`);
                    if (onWaitlistSuccess) onWaitlistSuccess();
                }
                
                const failures = results.filter(r => !r.success);
                if (failures.length > 0) {
                    setError(`Failed to add some students: ${failures.map(f => f.student).join(', ')}`);
                }
                
                setLoading(false);
                return;
            }
            
            // Single student mode (existing logic)
            // Check if selected student is already on waitlist
            if (!isNewStudent && selectedStudent && studentsOnWaitlist.includes(selectedStudent.toLowerCase())) {
                setError('This student is already on the waitlist for this course');
                setLoading(false);
                return;
            }

            let waitlistData;

            if (isGuardian) {
                // For guardians, use simplified data
                if (isNewStudent) {
                    // New student - minimal info needed
                    // For new student, create a managed email
                    const managedEmail = `${studentName.replace(/\s+/g, '.').toLowerCase()}@managed.student`;
                    waitlistData = {
                        courseId,
                        email: managedEmail, // Use managed email for new students
                        studentName: studentName,
                        guardianEmail: userEmail,
                        isGuardianBooking: true,
                        formData: {
                            student_name: studentName,
                            parent_email: userEmail, // Use guardian's email
                            school: studentInfo.school,
                            year_level: studentInfo.yearLevel,
                            date_of_birth: studentInfo.dateOfBirth,
                            gender: studentInfo.gender,
                            medical_conditions: studentInfo.medicalConditions,
                            dietary_requirements: studentInfo.dietaryRequirements,
                            swimming_ability: studentInfo.swimmingAbility,
                            // Contact details inherited from guardian
                            contact_1: user?.phoneNumber || '',
                            emergency_contact_name: user?.name || userEmail,
                            emergency_contact_number: user?.phoneNumber || ''
                        }
                    };
                } else {
                    // Existing student from guardian's list
                    const student = guardianStudents.find(s => s.email === selectedStudent);
                    waitlistData = {
                        courseId,
                        email: selectedStudent,
                        studentName: student?.name || student?.firstName + ' ' + student?.lastName,
                        guardianEmail: userEmail,
                        isGuardianBooking: true,
                        formData: {
                            student_name: student?.name || student?.firstName + ' ' + student?.lastName,
                            parent_email: userEmail,
                            // Use existing student data if available, but don't require it
                            ...(student?.phoneNumber && { contact_1: student.phoneNumber })
                        }
                    };
                }
            } else {
                // Non-guardian standard form
                waitlistData = {
                    courseId,
                    email: userEmail,
                    name: user?.name || user?.firstName + ' ' + user?.lastName,
                    formData: {
                        student_name: user?.name || user?.firstName + ' ' + user?.lastName,
                        student_email: userEmail
                    }
                };
            }

            await joinWaitlist(waitlistData);
            
            // Clear form and show success
            setStudentName('');
            setSelectedStudent('');
            setIsNewStudent(false);
            
            // Notify parent component
            if (onWaitlistSuccess) {
                onWaitlistSuccess();
            }
            
            // Show toast notification
            toast.success(
                isGuardian 
                    ? `${isNewStudent ? studentName : 'Student'} added to waitlist successfully!`
                    : "Successfully joined the waitlist!"
            );
        } catch (err) {
            console.error("Waitlist failed:", err);
            const errorMessage = err?.response?.data?.message || err?.message || "Failed to join waitlist. Please try again.";
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Guardian-specific form
    if (isGuardian) {
        // ONE-BUTTON EXPERIENCE for failed bookings (single or multiple)
        if (failedStudents.length > 0) {
            const buttonText = failedStudents.length === 1 
                ? `Add ${failedStudents[0].name || 'Student'} to Waitlist`
                : `Add ${failedStudents.length} Students to Waitlist`;
            
            return (
                <div className="w-full max-w-md mx-auto">
                    {/* Show which students will be added */}
                    {failedStudents.length > 1 && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Students to waitlist:</p>
                            <ul className="text-sm text-gray-600 space-y-1">
                                {failedStudents.map(student => (
                                    <li key={student.email} className="flex items-center">
                                        <span className="mr-2">•</span>
                                        <span>{student.name || student.firstName}</span>
                                        {student.gender && (
                                            <span className="ml-2 text-xs text-gray-500">({student.gender})</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    <button
                        onClick={handleWaitlist}
                        disabled={loading}
                        className={`w-full py-4 px-6 rounded-lg text-white font-semibold text-lg transition-all ${
                            loading 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-green-600 hover:bg-green-700 transform hover:scale-105 shadow-lg'
                        }`}
                    >
                        {loading 
                            ? "Adding to Waitlist..." 
                            : buttonText
                        }
                    </button>
                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-200 rounded-lg">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}
                </div>
            );
        }
        
        // Regular form for other scenarios
        return (
            <form onSubmit={handleWaitlist} className="bg-white p-6 shadow-lg rounded-lg w-full max-w-md mx-auto my-8">
                <h3 className="text-xl font-bold mb-4 text-gray-700">
                    {`Add Student${isMultiStudentMode ? 's' : ''} to Waitlist`}
                </h3>
                
                {/* Custom message if booking failed */}
                {customMessage && (
                    <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg">
                        {customMessage}
                    </div>
                )}
                
                <p className="mb-4 text-gray-600">
                    {isMultiStudentMode 
                        ? 'Select which students you want to add to the waitlist:'
                        : 'This course is currently full. Add your student to the waitlist to be notified when a spot becomes available.'
                    }
                </p>

                {error && <div className="text-red-600 mb-4 p-3 bg-red-100 rounded-lg">{error}</div>}
                
                {/* Multi-student selection for failed bookings */}
                {isMultiStudentMode && (
                    <div className="mb-6 space-y-2">
                        <label className="block text-gray-700 mb-2 font-medium">
                            Select students to add to waitlist:
                        </label>
                        {failedStudents.map(student => {
                            const isOnWaitlist = studentsOnWaitlist.includes(student.email?.toLowerCase());
                            const isSelected = selectedStudentsForWaitlist.includes(student.email);
                            
                            return (
                                <div key={student.email} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <input
                                        type="checkbox"
                                        id={`student-${student.email}`}
                                        checked={isSelected}
                                        disabled={isOnWaitlist}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedStudentsForWaitlist([...selectedStudentsForWaitlist, student.email]);
                                            } else {
                                                setSelectedStudentsForWaitlist(
                                                    selectedStudentsForWaitlist.filter(email => email !== student.email)
                                                );
                                            }
                                        }}
                                        className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label 
                                        htmlFor={`student-${student.email}`}
                                        className={`flex-1 ${isOnWaitlist ? 'text-gray-400' : 'text-gray-700'}`}
                                    >
                                        <span className="font-medium">{student.name}</span>
                                        {student.gender && (
                                            <span className="ml-2 text-sm text-gray-500">
                                                ({student.gender})
                                            </span>
                                        )}
                                        {isOnWaitlist && (
                                            <span className="ml-2 text-sm text-red-600">
                                                (Already on waitlist)
                                            </span>
                                        )}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* One-click waitlist for failed student */}
                {!isMultiStudentMode && failedStudents.length === 1 && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h4 className="font-semibold text-gray-900">
                                    {failedStudents[0].name || failedStudents[0].firstName || 'Student'}
                                </h4>
                                {failedStudents[0].gender && (
                                    <p className="text-sm text-gray-600">
                                        Gender: {failedStudents[0].gender}
                                    </p>
                                )}
                            </div>
                            <div className="text-right">
                                <span className="text-xs text-blue-600 font-medium">
                                    Ready to waitlist
                                </span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600">
                            Click below to add to the waitlist. We'll notify you when a spot becomes available.
                        </p>
                    </div>
                )}

                {/* Single Student Selection - hide in multi-student mode AND when we have a pre-selected failed student */}
                {!isMultiStudentMode && failedStudents.length === 0 && (
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">Select Student:</label>
                        
                        {guardianStudents.length > 0 && (
                            <div className="mb-3">
                                <select
                                    value={selectedStudent}
                                    onChange={(e) => {
                                        setSelectedStudent(e.target.value);
                                        setIsNewStudent(e.target.value === 'new');
                                    }}
                                    className="border border-gray-300 p-2 w-full rounded"
                                    required={!isNewStudent}
                                >
                                    <option value="">-- Select a student --</option>
                                    {guardianStudents.map(student => {
                                        const isOnWaitlist = studentsOnWaitlist.includes(student.email?.toLowerCase());
                                        return (
                                            <option 
                                                key={student.email} 
                                                value={student.email}
                                                disabled={isOnWaitlist}
                                            >
                                                {student.name || `${student.firstName} ${student.lastName}`}
                                                {isOnWaitlist ? ' (Already on waitlist)' : ''}
                                            </option>
                                        );
                                    })}
                                    <option value="new">+ Add New Student</option>
                                </select>
                            </div>
                        )}
                        
                        {(guardianStudents.length === 0 || isNewStudent) && failedStudents.length === 0 && (
                            <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Student Name *</label>
                                <input
                                    type="text"
                                    placeholder="Enter student's full name"
                                    value={studentName}
                                    onChange={(e) => setStudentName(e.target.value)}
                                    className="border border-gray-300 p-2 w-full rounded"
                                    required
                                />
                            </div>
                            
                            {/* Student Information Fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">School *</label>
                                    <input
                                        type="text"
                                        placeholder="School name"
                                        value={studentInfo.school}
                                        onChange={(e) => setStudentInfo({...studentInfo, school: e.target.value})}
                                        className="border border-gray-300 p-2 w-full rounded text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Year Level *</label>
                                    <select
                                        value={studentInfo.yearLevel}
                                        onChange={(e) => setStudentInfo({...studentInfo, yearLevel: e.target.value})}
                                        className="border border-gray-300 p-2 w-full rounded text-sm"
                                        required
                                    >
                                        <option value="">Select year</option>
                                        <option value="Year 7">Year 7</option>
                                        <option value="Year 8">Year 8</option>
                                        <option value="Year 9">Year 9</option>
                                        <option value="Year 10">Year 10</option>
                                        <option value="Year 11">Year 11</option>
                                        <option value="Year 12">Year 12</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                                    <input
                                        type="date"
                                        value={studentInfo.dateOfBirth}
                                        onChange={(e) => setStudentInfo({...studentInfo, dateOfBirth: e.target.value})}
                                        className="border border-gray-300 p-2 w-full rounded text-sm"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                                    <select
                                        value={studentInfo.gender}
                                        onChange={(e) => setStudentInfo({...studentInfo, gender: e.target.value})}
                                        className="border border-gray-300 p-2 w-full rounded text-sm"
                                        required
                                    >
                                        <option value="">Select gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                        <option value="Prefer not to say">Prefer not to say</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
                                <textarea
                                    placeholder="Any medical conditions we should be aware of?"
                                    value={studentInfo.medicalConditions}
                                    onChange={(e) => setStudentInfo({...studentInfo, medicalConditions: e.target.value})}
                                    className="border border-gray-300 p-2 w-full rounded text-sm"
                                    rows="2"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Requirements</label>
                                <input
                                    type="text"
                                    placeholder="Any dietary requirements or allergies?"
                                    value={studentInfo.dietaryRequirements}
                                    onChange={(e) => setStudentInfo({...studentInfo, dietaryRequirements: e.target.value})}
                                    className="border border-gray-300 p-2 w-full rounded text-sm"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Swimming Ability</label>
                                <select
                                    value={studentInfo.swimmingAbility}
                                    onChange={(e) => setStudentInfo({...studentInfo, swimmingAbility: e.target.value})}
                                    className="border border-gray-300 p-2 w-full rounded text-sm"
                                >
                                    <option value="">Select ability</option>
                                    <option value="Cannot swim">Cannot swim</option>
                                    <option value="Beginner">Beginner</option>
                                    <option value="Intermediate">Intermediate</option>
                                    <option value="Advanced">Advanced</option>
                                </select>
                            </div>
                            
                            <p className="text-sm text-gray-500 mt-2">
                                * Contact details and billing will use your guardian account information
                            </p>
                        </div>
                    )}
                    </div>
                )}

                {/* Only show note if NOT a failed booking scenario */}
                {failedStudents.length === 0 && (
                    <div className="bg-blue-50 p-3 rounded mb-4">
                        <p className="text-sm text-blue-700">
                            <strong>Note:</strong> As a guardian, your contact and billing information will be used for this waitlist entry. 
                            No additional student contact details are required.
                        </p>
                    </div>
                )}

                <button
                    type="submit"
                    className={`w-full py-3 px-4 rounded text-white font-semibold ${loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                    disabled={loading || (!selectedStudent && !studentName && failedStudents.length === 0)}
                >
                    {loading ? "Adding to Waitlist..." : 
                     failedStudents.length === 1 ? `Add ${failedStudents[0].name || 'Student'} to Waitlist` :
                     isMultiStudentMode ? "Add Selected Students to Waitlist" :
                     "Add to Waitlist"}
                </button>
            </form>
        );
    }

    // Standard form for non-guardians
    return (
        <form onSubmit={handleWaitlist} className="bg-white p-6 shadow-lg rounded-lg w-full max-w-md mx-auto my-8">
            <h3 className="text-xl font-bold mb-4 text-gray-700">Join Waitlist</h3>
            <p className="mb-4 text-gray-600">
                This course is currently full. Join the waitlist to be notified when a spot becomes available.
            </p>

            {error && <div className="text-red-600 mb-4 p-3 bg-red-100 rounded-lg">{error}</div>}

            <div className="bg-gray-50 p-4 rounded mb-4">
                <p className="text-sm text-gray-600">
                    You'll be added to the waitlist as <strong>{user?.name || userEmail}</strong>
                </p>
            </div>

            <button
                type="submit"
                className={`w-full py-2 px-4 rounded text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                disabled={loading}
            >
                {loading ? "Joining Waitlist..." : "Join Waitlist"}
            </button>
        </form>
    );
}

export default GuardianWaitlistForm;